import React, { useEffect } from "react";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Tooltip } from "primereact/tooltip";
import { Dropdown } from "primereact/dropdown";

import useMyProfile from "./useMyProfile";
import ServiceAccessTable from "./ServiceAccessTable";
import CustomeButton from "../../../components/Button/index";
import timezoneslist from "../../../assets/timezone-list.json";
import { useAuth } from "@workos-inc/authkit-react";
import TimeZoneDropdown from "~/components/TimeZoneSelector";
import { useForm, Controller } from "react-hook-form";
import Notification from "../../../components/entities/notification/Notification";
import AdminOnly from "~/components/AdminOnly";

const MyProfile = () => {
  const { signOut } = useAuth();

  const {
    userdata,
    myentityaccess,
    serviceAvailable,
    paginationFirst,
    paginationRows,
    isUpateTimezone,
    paginationOnchange,
    handleUpdateTimezone,
    isEditing,
    setIsEditing,
    disabled,
    handleChange,
    userInfo,
    message,
    setMessage,
    successMessage,
    setSuccessMessage,
    handleUpdateUserInformation,
    loading,
    handleDiscard,
    role: userRole,
  } = useMyProfile();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      first_name: userdata?.first_name || "",
      other_name: userdata?.other_name || "",
      last_name: userdata?.last_name || "",
      preferred_name: userdata?.preferred_name || "",
    },
  });

  let role = "Customer User";

  switch (userRole || userdata?.role) {
    case "LSP_USER":
      role = "Local Team User";
      break;
    case "CUSTOMER":
      role = "Customer User";
      break;
    case "GXM_ADMIN":
      role = "GXM Admin User";
      break;
    case "GXM_USER":
      role = "GXM User";
      break;
    case "GXM_MANAGER":
      role = "GXM Manager";
      break;
    case "LSP_MANAGER":
      role = "Local Team Manager";
      break;
    case "CUSTOMER_MANAGER":
      role = "Customer Manager";
      break;
  }

  const handleLogout = () => {
    const returnTo = window.location.origin;
    console.log(`signing out, returning to ${returnTo}`);
    signOut({ returnTo });
  };

  return (
    <>
      <Card className="gen-card mt-0 h-full">
        <div className="general-info-container flex justify-content-between formgrid grid grid-nogutter">
          <div className="flex-grow">
            <p className="profile-heading">My Account</p>
            <p className="profile-para">
              Manage your personal information and account preferences here.
            </p>
          </div>
          <div className="items-center flex align-items-center">
            <CustomeButton
              label="Log Out"
              variant="outlined"
              className="mr-2"
              onClick={handleLogout}
              pi-sign-out
              icon="pi pi-sign-out"
              iconPos="right"
            />
            <AdminOnly>
              {isEditing ? (
                <>
                  <CustomeButton
                    label={"Discard"}
                    className={`mr-2`}
                    onClick={() => {
                      handleDiscard();
                    }}
                  />
                  <CustomeButton
                    label={"Save"}
                    className={`mr-2 ${disabled ? "disable_btn" : null}`}
                    variant="contained"
                    onClick={() => {
                      handleUpdateUserInformation();
                    }}
                    disabled={disabled}
                    icon="pi pi-pencil"
                    iconPos="right"
                    loading={loading}
                  />
                </>
              ) : (
                <CustomeButton
                  label={"Edit"}
                  className="mr-2"
                  variant="contained"
                  onClick={() => {
                    setIsEditing(!isEditing);
                  }}
                  icon="pi pi-pencil"
                  iconPos="right"
                />
              )}
            </AdminOnly>
          </div>
          <Notification
            message={message}
            successMessage={successMessage}
            setMessage={setMessage}
            setSuccessMessage={setSuccessMessage}
          />
          <div className="formgrid grid">
            <div className="field col-12">
              <p className="user-role-heading">General Information</p>
            </div>
            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Given Name(s) *{" "}
                <i className="pi pi-info-circle" id="given-name-info"></i>
                <Tooltip
                  target="#given-name-info"
                  content="This is usually the person's first name or the name they usually go by."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="first_name"
                control={control}
                rules={{ required: "Given name is required." }}
                render={({ field }) => (
                  <InputText
                    {...field}
                    value={userInfo?.first_name || ""}
                    onChange={(e) => {
                      handleChange("first_name", e.target.value);
                      field.onChange(e);
                    }}
                    placeholder="Given Name"
                    type="text"
                    disabled={!isEditing}
                    className={`appearance-none outline-none w-full ${
                      errors.first_name ? "p-invalid" : ""
                    } ${!isEditing ? "disabled-input" : ""}`}
                  />
                )}
              />
              {errors.first_name && (
                <small className="p-error">{errors.first_name.message}</small>
              )}
            </div>

            <div className="field col-12 md:col-6 mb-0">
              <label className="input-label">
                Other Name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="other-name-info"
                ></i>
                <Tooltip
                  target="#other-name-info"
                  content="Any middle or extra names that the employee has. You can leave this blank if it doesn’t apply."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="other_name"
                control={control}
                render={({ field }) => (
                  <InputText
                    {...field}
                    value={userInfo?.other_name || ""}
                    onChange={(e) => {
                      handleChange("other_name", e.target.value);
                      field.onChange(e.target.value);
                    }}
                    placeholder="Other Name"
                    type="text"
                    disabled={!isEditing}
                    className={`appearance-none outline-none w-full ${
                      !isEditing ? "disabled-input" : ""
                    }`}
                  />
                )}
              />
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Surname(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="sur-name-info"
                ></i>
                <Tooltip
                  target="#sur-name-info"
                  content="This is usually a family name or last name. If employee has more than one, include them here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="last_name"
                control={control}
                render={({ field }) => (
                  <InputText
                    {...field}
                    value={userInfo?.last_name || ""}
                    onChange={(e) => {
                      handleChange("last_name", e.target.value);
                      field.onChange(e);
                    }}
                    placeholder="Surname(s)"
                    type="text"
                    disabled={!isEditing}
                    className={`appearance-none outline-none w-full ${
                      errors.last_name ? "p-invalid" : ""
                    } ${!isEditing ? "disabled-input" : ""}`}
                  />
                )}
              />
              {errors.last_name && (
                <small className="p-error">{errors.last_name.message}</small>
              )}
            </div>

            <div className="field col-12 md:col-12 mb-0 mt-3">
              <label className="input-label">
                Preferred name(s)
                <i
                  className="ml-2 pi pi-info-circle info-icon"
                  id="preffered-name-info"
                ></i>
                <Tooltip
                  target="#preffered-name-info"
                  content="If the employee wants to be called by a different name than their given name, put that here."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <Controller
                name="preferred_name"
                control={control}
                render={({ field }) => (
                  <InputText
                    {...field}
                    value={userInfo?.preferred_name || ""}
                    onChange={(e) => {
                      handleChange("preferred_name", e.target.value);
                      field.onChange(e.target.value);
                    }}
                    placeholder="Preferred Name or Nickname"
                    type="text"
                    disabled={!isEditing}
                    className={`appearance-none outline-none w-full ${
                      !isEditing ? "disabled-input" : ""
                    }`}
                  />
                )}
              />
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">Email</label>
              <InputText
                value={userdata?.email}
                placeholder="evaccaro@company.com"
                name="email"
                type="text"
                disabled
                className="appearance-none outline-none w-full disabled-input"
              />
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">Organization</label>
              <div className="icon-field">
                <InputText
                  value={userdata?.organization}
                  placeholder="Organisation"
                  type="text"
                  name="organisation"
                  disabled
                  className="appearance-none outline-none w-full disabled-input"
                />
                <i className="pi pi-lock input-icon"></i>
              </div>
            </div>

            <div className="field col-12 md:col-6 mb-0 mt-3">
              <label className="input-label">User Role</label>
              <div className="icon-field">
                <InputText
                  placeholder="User Role"
                  value={role}
                  disabled
                  type="text"
                  name="user_role"
                  className="appearance-none outline-none w-full disabled-input"
                />
                <i className="pi pi-lock input-icon"></i>
              </div>
            </div>

            <div className=" col-12 md:col-12 mt-2">
              <p className="location-heading">Location and Time Zone</p>
            </div>
            <div className="field col-12 md:col-6 mb-2">
              <label className="input-label">
                Time Zone *{" "}
                <i className="pi pi-info-circle" id="given-name-info"></i>
                <Tooltip
                  target="#given-name-info"
                  content="Select your time zone for accurate scheduling and project timing."
                  position="right"
                  className="custom-tooltip tooltip-custom-format"
                />
              </label>
              <div className="icon-field">
                <TimeZoneDropdown
                  timezones={timezoneslist}
                  value={userdata?.timezone}
                  onChange={handleUpdateTimezone}
                  loading={isUpateTimezone}
                  disabled={isUpateTimezone}
                />
              </div>
            </div>
          </div>

          <ServiceAccessTable
            entityaccess={myentityaccess}
            services={serviceAvailable}
            paginationFirst={paginationFirst}
            paginationRows={paginationRows}
            paginationOnchange={paginationOnchange}
            role={userRole || userdata?.role}
          />
        </div>
      </Card>
    </>
  );
};

export default MyProfile;
